import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Recaptcha from 'react-google-recaptcha';
import TagManager from 'react-gtm-module';
import { useFormik } from 'formik';
import Cookies from 'js-cookie';
import { maskJs } from 'mask-js';
import * as Yup from 'yup';

// hooks and utils
import { useProductFlow } from 'hooks/useProductFlow';
import { useCart } from 'hooks/useCart';
import useSessionStorage from 'hooks/useSessionStorage';
import { useTranslations } from 'hooks/useTranslations';
import { getOrCreateCartId } from 'utils/cartId';
import {
  getFormatDate,
  isBirthDateHigherOrEqualThan18Years,
  unformatDate,
  birthDateValidation,
} from './utils';
import { sendDataToAnalytics } from 'utils/sendDataToAnalytics';
import { getErrorMessage, getErrorTitle } from 'utils/errors';
import { getAnalyticsGclid } from 'utils/getAnalyticsGclid';
import { getMetaTrackingParams } from 'utils/getMetaTrackingParams';
import { getGoogleTrackingParams } from 'utils/getGoogleTrackingParams';
import ReturnFirstCapital from 'utils/returnFirstCapital';
import { errorsMessage, formatValueBeforeSend } from 'utils/globalFunctions';
import * as validate from 'utils/inputValidate';
import { getProductV3CaptchaKey } from 'utils/getProductCaptchaKey';
import { getUtmMetadata } from 'utils/getUtmMetadata';

// services
import { createLead } from 'services/repositories/leads';
import { validatePhone } from 'utils/PhoneNumberValidation';
import { parsePhoneNumber } from 'react-phone-number-input';

// components
import Steps from 'componentsV2/Steps';
import PhoneNumberInput from 'componentsV2/PhoneNumberInput';
import PoliticsAndTerms from 'componentsV2/PoliticsAndTerms';
import SecurityFooter from 'componentsV2/DS/SecurityFooter';
import {
  DsButton,
  DsTextField,
  DsCheckbox,
  DsTypography,
} from 'componentsV2/DS';

// constants
import { DATA_FORM, DATA_LEAD } from '../../../constants/';
import { CAPTCHA_TOKEN_STAGING } from 'config/captchaStaging';

// styles
import { ErrorBoxContainer, Form, TitleBox, Wrapper } from './styles';

// types
import { type AcceptedTerm, type LeadData } from 'types';
import { useRouterQuery } from 'hooks/useRouterQuery';
import { FeedbackBox } from 'componentsV2/DS/FeedbackBox';

interface LeadScreenProps {
  contactValues?: {
    name: string;
    email: string;
    phone: string;
  };
  onSubmitSuccess: (response: any, data: any) => void | Promise<unknown>;
}

export default function LeadScreen({
  contactValues,
  onSubmitSuccess,
}: LeadScreenProps) {
  const {
    t,
    getLabel,
    getPlaceholder,
    getError,
    getValidationError,
    getDocumentType,
    getResponsibleDocumentLabel,
    getResponsibleDocumentPlaceholder,
    locale,
    tButtons,
  } = useTranslations();

  const [dataForm] = useSessionStorage(DATA_FORM);
  const [leadData] = useSessionStorage<LeadData>(DATA_LEAD);

  const router = useRouter();
  const routerQuery = useRouterQuery();
  const { currentStep, productFlow, getStepsCount } = useProductFlow();
  const context = useCart();

  const { currentStepCount, totalStepsCount } = getStepsCount({
    isUpsellFlow: false,
  });

  const { area, setLoading, productBusiness, productCode, legacyProductTheme } =
    context;

  const RECAPTCHA_SITE_KEY = getProductV3CaptchaKey(
    currentStep?.props?.plan?.business || productBusiness
  );

  const showBirthDateInput = currentStep?.props?.showBirthDateInput;
  const isTrialFlow =
    productFlow?.id == 'trial' || productFlow?.id == 'trial-already-used';
  const showDocumentInput =
    currentStep?.props?.showDocumentInput || isTrialFlow;
  const documentType =
    currentStep?.props?.documentTypeInput || getDocumentType();

  const documentConfig = {
    cpf: {
      label: getLabel('document'),
      placeholder: getPlaceholder('document'),
      maxLength: 14,
      mask: (value: string) => maskJs('999.999.999-99', value),
    },
    cnpj: {
      label: getLabel('cnpj'),
      placeholder: getPlaceholder('cnpj'),
      maxLength: 18,
      mask: (value: string) => maskJs('99.999.999/9999-99', value),
    },
    dni: {
      label: getLabel('dni'),
      placeholder: getPlaceholder('dni'),
      maxLength: 8,
      mask: (value: string) => value.replace(/\D/g, '').slice(0, 8),
    },
  }[documentType];

  const termsUrl =
    currentStep?.props?.termsUrl ||
    legacyProductTheme.data?.links?.[context?.environment]?.terms;
  const politicsUrl =
    currentStep?.props?.politicsUrl ||
    legacyProductTheme.data?.links?.[context?.environment]?.politics;

  const productHavePolitcsAndTerms = termsUrl || politicsUrl;
  const stepsNumber =
    totalStepsCount ||
    productFlow?.props?.config?.stepsNumber ||
    legacyProductTheme.data?.stepsNumber;

  const [errorMessage, setErrorMessage] = useState({ title: '', message: '' });
  const [startPulseAnimation, setStartPulseAnimation] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [shouldShowResponsibleInputs, setShouldShowResponsibleInputs] =
    useState(false);

  const [, setDefaultSelectedPlan] = useSessionStorage('defaultSelectedPlan');
  const captchaRef = useRef<Recaptcha>();
  // FIN-8011 (RSK-02): um envio por vez. Fica preso do clique até o fim do
  // onSubmitSuccess (POST /lead e, só em link de oferta que já veio
  // encerrado/não iniciado, a conferência no BFF) e solta depois, com ou sem
  // erro, como o botão já fazia. É um ref porque o estado do botão só muda no
  // próximo render e um segundo clique chega antes dele.
  const submitInFlightRef = useRef(false);

  const releaseSubmit = () => {
    submitInFlightRef.current = false;
    setDisableButton(false);
    setLoading(false);
  };

  const showResponsibleInputs =
    currentStep?.props?.showResponsibleInputs && shouldShowResponsibleInputs;

  const showInstagramInput = currentStep?.props?.showInstagramInput;
  const showCompanyNameInput = currentStep?.props?.showCompanyNameInput;

  const showTermsUrl = !currentStep?.props?.hideTermsUrl;

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(getError('required'))
      .test('nome-valido', getValidationError('NAME').format, (value) => {
        return !validateInput({ fieldtype: 'NAME', value, lang: locale });
      })
      .trim(),
    email: Yup.string()
      .test(
        'email-sem-caracteres-especiais',
        getError('invalidEmailSpecialChars'),
        (value) => {
          if (!value) return true;
          return /^[a-zA-Z0-9@._%+\-!#$&'*/=?^`{|}~]+$/.test(value);
        }
      )
      .email(getError('invalidEmail'))
      .required(getError('required')),
    phone: Yup.string()
      .required(getError('required'))
      .test('phone-valido', getError('invalidPhone'), (value) => {
        if (!value) return true;
        const detectedCountry =
          parsePhoneNumber(value)?.country ||
          { 'es-PE': 'PE', 'pt-BR': 'BR' }[locale] ||
          'BR';
        return !!validatePhone(value, detectedCountry);
      }),
    ...(showInstagramInput && {
      instagram: Yup.string().required(getError('required')),
    }),
    ...(showBirthDateInput && {
      birthDate: birthDateValidation(productCode, getError).required(
        getError('required')
      ),
    }),
    ...(showDocumentInput && {
      cpf: Yup.string()
        .required(getError('required'))
        .test(
          'document-valido',
          getValidationError(documentType).format,
          (value) => {
            return !validateInput({
              fieldtype: documentType.toUpperCase(),
              value,
              lang: locale,
            });
          }
        ),
    }),
    ...(showResponsibleInputs && {
      responsibleName: Yup.string()
        .required(getError('required'))
        .test(
          'nome-responsavel-valido',
          getValidationError('NAME').format,
          (value) => {
            return !validateInput({ fieldtype: 'NAME', value, lang: locale });
          }
        ),
      responsibleDocument: Yup.string()
        .required(getError('required'))
        .test(
          'document-responsavel-valido',
          getValidationError(getDocumentType()).format,
          (value) => {
            return !validateInput({
              fieldtype: getDocumentType().toUpperCase(),
              value,
              lang: locale,
            });
          }
        ),
    }),
    politicsChecked: productHavePolitcsAndTerms
      ? Yup.boolean().oneOf([true], getValidationError('POLITICS').format)
      : Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      email: contactValues?.email || dataForm?.email || '',
      name: contactValues?.name || dataForm?.name || '',
      phone: contactValues?.phone || dataForm?.phone || '',
      ...(showInstagramInput && { instagram: '' }),
      ...(showBirthDateInput && {
        birthDate: leadData?.birthDate ? unformatDate(leadData.birthDate) : '',
      }),
      ...(showDocumentInput && { cpf: leadData?.cpf || '' }),
      responsibleName: leadData?.responsible?.name || '',
      responsibleDocument: leadData?.responsible?.cpf || '',
      politicsChecked: false,
    },
    validationSchema,
    onSubmit: () => verifyCaptcha(),
    validateOnMount: true,
  });
  const birthDateValue = formik.values.birthDate;

  useEffect(() => {
    if (birthDateValue && birthDateValue.length === 10) {
      setShouldShowResponsibleInputs(
        !isBirthDateHigherOrEqualThan18Years(birthDateValue)
      );
    }
  }, [birthDateValue]);

  // Initialize cart ID when user enters the checkout flow
  useEffect(() => {
    getOrCreateCartId();
  }, []);

  const validateInput = ({ fieldtype, value, lang }) => {
    const validateObj = validate.inputValidate(fieldtype, value, lang);
    return value === '' ? '' : validateObj.errorMessage;
  };

  const getDefaultCountryByLocale = (locale: string) => {
    const localeToCountryMap = {
      'es-PE': 'PE',
      'pt-BR': 'BR',
    };
    return localeToCountryMap[locale] || 'BR';
  };

  const handleSubmit = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (disableButton || submitInFlightRef.current) return null;

    if (!formik.isValid) {
      formik.setTouched(
        Object.keys(formik.values).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
        true
      );
      return;
    }

    formik.handleSubmit();
  };

  const sendLeadForm = async ({
    name,
    email,
    phone,
    cpf,
    instagram,
    captchaToken,
  }) => {
    Cookies.set('email', email);

    const gclid = getAnalyticsGclid(routerQuery as Record<string, string>);
    const metaTracking = getMetaTrackingParams(
      routerQuery as Record<string, string>
    );
    const googleTracking = getGoogleTrackingParams(
      routerQuery as Record<string, string>,
      gclid
    );

    const hasPlanId = !!currentStep?.props?.plan?.id;

    const saveLeadDataParams = {
      name: name?.trim(),
      email,
      phone,
      instagram,
      area,
      source: routerQuery.source as string,
      operation: 'BR',
      seller: routerQuery.seller as string,
      voucher: routerQuery.voucher as string,
      captchaToken,
      analytics: { gclid },
      business: currentStep?.props?.plan?.business || productBusiness,
      productCode: currentStep?.props?.plan?.productCode || productCode,
      ...(formik.values.politicsChecked && {
        acceptedTerms: [
          ...(termsUrl
            ? [
                {
                  label: currentStep?.props?.termsContent || 'Termos de Uso',
                  url: termsUrl,
                  acceptedAt: new Date().toISOString(),
                },
              ]
            : []),
          ...(politicsUrl
            ? [
                {
                  label: 'Política de Privacidade',
                  url: politicsUrl,
                  acceptedAt: new Date().toISOString(),
                },
              ]
            : []),
        ],
      }),
      metadata: {
        GAClientId: context._ga,
        gclid,
        ...getUtmMetadata(routerQuery as Record<string, string>),
        ...metaTracking,
        ...googleTracking,
      },
      ...(showDocumentInput && {
        document: {
          type: documentType,
          value: formatValueBeforeSend(cpf),
        },
      }),
      ...(hasPlanId
        ? {
            planId: currentStep?.props?.plan?.id,
          }
        : {
            plan: currentStep?.props?.plan?.plan || 'default',
            planType: currentStep?.props?.plan?.planType,
          }),
    };

    let navigation: void | Promise<unknown>;

    try {
      const saveLeadDataResponse = await createLead(saveLeadDataParams);

      const tagManagerArgs = {
        dataLayer: {
          event: 'generate_lead',
          phone_number: phone,
          email,
          lead_type: 'formulario_site',
        },
      };

      TagManager.dataLayer(tagManagerArgs);

      if (!saveLeadDataResponse.powerHousePlansEnabledToPurchase?.length) {
        setDefaultSelectedPlan(currentStep?.props?.plan || 'default');
      }

      navigation = onSubmitSuccess(
        {
          ...saveLeadDataResponse,
          ...(showBirthDateInput && {
            birthDate: getFormatDate(formik.values.birthDate as string),
          }),
          ...(showDocumentInput && {
            cpf: formik.values.cpf,
          }),
          ...(showResponsibleInputs && {
            responsible: {
              name: formik.values.responsibleName,
              cpf: formik.values.responsibleDocument,
            },
          }),
        },
        {
          ...saveLeadDataParams,
          ...(showResponsibleInputs && {
            responsible: {
              name: formik.values.responsibleName,
              document: {
                type: getDocumentType(),
                value: formik.values.responsibleDocument,
              },
            },
          }),
        }
      );
    } catch (err) {
      const errorOperation = { 'es-PE': 'PE', 'pt-BR': 'BR' }[locale] || 'BR';
      setErrorMessage({
        message: getErrorMessage(
          errorOperation,
          err?.response?.data?.messageCode
        ),
        title: getErrorTitle(errorOperation, err?.response?.data?.messageCode),
      });
      releaseSubmit();
      return;
    } finally {
      if (context.environment?.toUpperCase() !== 'TEST') {
        captchaRef.current?.reset();
      }
    }

    try {
      await navigation;
    } finally {
      // A navegação já foi disparada pelo onSubmitSuccess (sem esperar a
      // troca de página, como antes da trava).
      releaseSubmit();
    }
  };

  const verifyCaptcha = async () => {
    const hasFormikErrors = Object.keys(formik.errors).length !== 0;
    const isTestEnvironment = context?.environment?.toUpperCase() === 'TEST';

    if (
      !hasFormikErrors &&
      !formik.values.politicsChecked &&
      productHavePolitcsAndTerms
    ) {
      setErrorMessage({
        title: 'Política de Privacidade',
        message:
          'Para prosseguir, aceite a Política de Privacidade no checkbox acima do botão.',
      });
      setStartPulseAnimation(true);
      return null;
    }
    if (submitInFlightRef.current) return null;
    submitInFlightRef.current = true;
    setDisableButton(true);
    setLoading(true);
    sendDataToAnalytics(
      {
        userEmail: context?.userEmail,
        source: routerQuery.source,
        seller: routerQuery.seller,
        system: context.productBusiness,
        _ga: context?._ga,
      },
      'lead',
      'click'
    );

    if (isTestEnvironment) {
      sendLeadForm({ ...formik.values, captchaToken: CAPTCHA_TOKEN_STAGING });
    } else {
      captchaRef.current?.execute();
    }
  };

  const handleCheckPolitics = (
    event: Event & {
      detail: {
        checked: boolean;
      };
    }
  ) => {
    const checked = event.detail.checked;
    formik.setFieldValue('politicsChecked', checked);
    if (checked) {
    } else {
    }
  };

  const formatValueOnChange = ({ inputName, inputValue }) => {
    const formatByInput = {
      email: inputValue?.replace(/ /g, '')?.trim()?.toLowerCase(),
      name: inputValue
        ?.replace(/\s{2,}/g, ' ')
        ?.split(' ')
        ?.map((item) => ReturnFirstCapital(item))
        ?.join(' ')
        ?.trim()
        ?.toString(),
      responsibleName: inputValue
        ?.replace(/\s{2,}/g, ' ')
        ?.split(' ')
        ?.map((item) => ReturnFirstCapital(item))
        ?.join(' ')
        ?.trim()
        ?.toString(),
      instagram: (() => {
        if (!inputValue) return '';
        const cleanValue = inputValue.replace(/\s/g, '')?.toLowerCase();
        const withoutAt = cleanValue.replace(/^@+/, '');
        return `@${withoutAt}`;
      })(),
    };

    formik.setFieldValue(inputName, formatByInput[inputName] || inputValue);
  };

  return (
    <Wrapper>
      <TitleBox>
        <DsTypography
          data-cy="@lead/page-title-message"
          dangerouslySetInnerHTML={{
            __html: currentStep?.props?.title || 'Informe seus dados',
          }}
          component="heading"
          size="medium"
          color="neutral-pure"
          font={context?.product === 'MS' ? 'secondary' : 'primary'}
        />
        <DsTypography
          data-cy="@lead/page-description-message"
          dangerouslySetInnerHTML={{
            __html:
              currentStep?.props?.subtitle ||
              'Complete suas informações para prosseguir para o pagamento.',
          }}
          component="paragraph"
          size="medium"
          color="neutral-pure"
          font="primary"
        />
      </TitleBox>

      <Steps
        data-cy="@lead/steps-box"
        stepsNumber={stepsNumber}
        currentStep={currentStepCount}
      />

      {errorMessage?.message && (
        <ErrorBoxContainer>
          <FeedbackBox
            title={errorMessage.title}
            message={errorMessage.message}
            onClose={() => setErrorMessage({ message: '', title: '' })}
            scrollToTop
            type="error"
          />
        </ErrorBoxContainer>
      )}

      <Form>
        {showResponsibleInputs && (
          <DsTypography
            data-cy="@lead/page-title-message"
            component="heading"
            size="small"
            color="neutral-pure"
            font={context?.product === 'MS' ? 'secondary' : 'primary'}
          >
            {t('section.studentData')}
          </DsTypography>
        )}

        <DsTextField
          id="name"
          onChange={({ detail }) =>
            detail?.id === 'name' &&
            formatValueOnChange({
              inputName: detail?.id,
              inputValue: detail?.value,
            })
          }
          onBlur={(event) => formik.handleBlur(event)}
          label={
            showCompanyNameInput ? getLabel('companyName') : getLabel('name')
          }
          placeholder={
            showCompanyNameInput
              ? getPlaceholder('companyName')
              : getPlaceholder('name')
          }
          value={formik.values.name}
          helperText={
            errorsMessage(formik, 'name') ||
            validateInput({
              fieldtype: 'NAME',
              value: formik.values.name,
              lang: 'pt-BR',
            })
          }
          name="name"
          type="text"
          data-cy="@lead/fullname-input"
          maxLength={200}
          data-testid="@jest/ds-name-text-field"
        />
        <DsTextField
          onChange={({ detail }) =>
            detail?.id === 'email' &&
            formatValueOnChange({
              inputName: detail?.id,
              inputValue: detail?.value,
            })
          }
          onBlur={(event) =>
            event?.detail?.id === 'email' && formik.handleBlur(event)
          }
          id="email"
          label={getLabel('email')}
          placeholder={getPlaceholder('email')}
          value={formik.values.email}
          helperText={errorsMessage(formik, 'email')}
          name="email"
          type="email"
          data-cy="@lead/email-input"
          maxLength={80}
          data-testid="@jest/ds-email-text-field"
        />
        <PhoneNumberInput
          value={formik.values.phone}
          fieldtype="PHONE"
          onChange={(phoneNumber) =>
            formatValueOnChange({ inputName: 'phone', inputValue: phoneNumber })
          }
          helperText={errorsMessage(formik, 'phone')}
          hasError={errorsMessage(formik, 'phone')}
          label={getLabel('phone')}
          dataCy="@lead/cellphone-input"
          dataCyErrorMessage="@lead/errorcellphone-message"
          defaultCountry={getDefaultCountryByLocale(locale)}
          placeholder={getPlaceholder('phone')}
          onBlur={null}
        />

        {showInstagramInput && (
          <DsTextField
            id="instagram"
            onChange={({ detail }) =>
              detail?.id === 'instagram' &&
              formatValueOnChange({
                inputName: detail?.id,
                inputValue: detail?.value,
              })
            }
            onBlur={(event) => formik.handleBlur(event)}
            label="Instagram"
            placeholder="@seunome"
            value={formik.values.instagram}
            helperText={errorsMessage(formik, 'instagram')}
            name="instagram"
            type="text"
            data-cy="@lead/instagram-input"
            maxLength={30}
            data-testid="@jest/ds-instagram-text-field"
          />
        )}

        {showBirthDateInput && (
          <DsTextField
            id="birthDate"
            label={getLabel('birthDate')}
            type="text"
            helperText={errorsMessage(formik, 'birthDate')}
            data-cy={`@${router.pathname?.slice(1)}/birthDate`}
            onChange={(event) => {
              formik.setFieldValue(
                event.target.id,
                maskJs('99/99/9999', event.target.value)
              );
            }}
            onBlur={formik.handleBlur}
            maxLength={10}
            placeholder={getPlaceholder('birthDate')}
            value={formik.values.birthDate}
            required
          />
        )}

        {showDocumentInput && (
          <DsTextField
            id="cpf"
            placeholder={documentConfig.placeholder}
            label={documentConfig.label}
            maxLength={documentConfig.maxLength}
            type="tel"
            helperText={
              errorsMessage(formik, 'cpf') ||
              validateInput({
                fieldtype: documentType.toUpperCase(),
                value: formik.values.cpf,
                lang: locale,
              })
            }
            data-cy={`@${router.pathname?.slice(1)}/cpf`}
            onChange={({ target: { id, value } }) => {
              formik.setFieldValue(id, documentConfig.mask(value));
            }}
            value={formik.values.cpf}
            required
          />
        )}

        {showResponsibleInputs && (
          <>
            <DsTypography
              component="heading"
              size="small"
              color="neutral-pure"
              font={context?.product === 'MS' ? 'secondary' : 'primary'}
            >
              {t('section.responsibleData')}
            </DsTypography>

            <DsTextField
              id="responsibleName"
              onChange={({ detail }) =>
                detail.id === 'responsibleName' &&
                formatValueOnChange({
                  inputName: detail?.id,
                  inputValue: detail?.value,
                })
              }
              onBlur={(event) => formik.handleBlur(event)}
              label={getLabel('responsibleName')}
              placeholder={getPlaceholder('responsibleName')}
              value={formik.values.responsibleName}
              helperText={
                errorsMessage(formik, 'responsibleName') ||
                validateInput({
                  fieldtype: 'NAME',
                  value: formik.values.responsibleName,
                  lang: 'pt-BR',
                })
              }
              name="responsibleName"
              type="text"
              data-cy="@lead/fullname-input"
              maxLength={200}
              data-testid="@jest/ds-name-text-field"
              required
            />

            <DsTextField
              id="responsibleDocument"
              placeholder={getResponsibleDocumentPlaceholder()}
              label={getResponsibleDocumentLabel()}
              maxLength={locale === 'es-PE' ? 8 : 14}
              onBlur={(event) => formik.handleBlur(event)}
              type="tel"
              helperText={
                errorsMessage(formik, 'responsibleDocument') ||
                validateInput({
                  fieldtype: getDocumentType().toUpperCase(),
                  value: formik.values.responsibleDocument,
                  lang: locale,
                })
              }
              data-cy={`@${router.pathname?.slice(1)}/responsibleDocument`}
              onChange={({ target: { id, value } }) => {
                const maskedValue =
                  locale === 'es-PE'
                    ? value.replace(/\D/g, '').slice(0, 8)
                    : maskJs('999.999.999-99', value);
                formik.setFieldValue(id, maskedValue);
              }}
              value={formik.values.responsibleDocument}
              required
            />
          </>
        )}
        {productHavePolitcsAndTerms && (
          <DsCheckbox
            id="checkbox"
            required
            checked={formik.values.politicsChecked}
            data-testid="@jest/ds-checkbox-input"
            pulse={startPulseAnimation}
            data-cy="@lead/agreewithprivacypolicy-checkbox"
            onChange={handleCheckPolitics}
          >
            <PoliticsAndTerms
              termsUrl={termsUrl}
              politicsUrl={politicsUrl}
              showTerms={showTermsUrl}
            />
          </DsCheckbox>
        )}

        <DsButton
          id="#LeadScreen/submit-button"
          data-testid="@jest/ds-continue-button"
          data-cy="@lead/continue-button"
          disabled={disableButton}
          onClick={handleSubmit}
        >
          {tButtons('continue')}
        </DsButton>
      </Form>
      <SecurityFooter dataCy="lead" />

      <Recaptcha
        size="invisible"
        ref={captchaRef}
        onChange={(captchaToken) => {
          sendLeadForm({ ...formik.values, captchaToken });
        }}
        sitekey={RECAPTCHA_SITE_KEY}
      />
    </Wrapper>
  );
}
